import { useState, useMemo } from 'react';
import { Loader2, Cable, AlertCircle, User, Check, ArrowRight, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  useCableSpools,
  useRecordCableUsage,
  useActiveUsers,
  useTransferOwnership,
} from '@/features/legacy/hooks/useCable';
import type { CableSpool } from '@/api/legacy';

interface CableUsageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientName: string;
}

export function CableUsageDialog({
  open,
  onOpenChange,
  clientName,
}: CableUsageDialogProps) {
  // Selection state
  const [selectedSpoolId, setSelectedSpoolId] = useState<string>('');
  const [amount, setAmount] = useState<string>('');

  // Filter state (both enabled by default)
  const [onlyMine, setOnlyMine] = useState(true);
  const [onlyNonEmpty, setOnlyNonEmpty] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Transfer state
  const [transferTo, setTransferTo] = useState<string>('');

  // Queries & mutations
  const { data: spools, isLoading } = useCableSpools();
  const { data: activeUsers } = useActiveUsers();
  const recordUsage = useRecordCableUsage();
  const transferOwnership = useTransferOwnership();

  // Filter spools
  const filteredSpools = useMemo(() => {
    if (!spools) return [];

    const query = searchQuery.toLowerCase().trim();

    return spools.filter((spool) => {
      // Search filter
      if (query) {
        const matchesNumber = spool.name.toLowerCase().includes(query);
        const matchesUser = spool.username.toLowerCase().includes(query);
        if (!matchesNumber && !matchesUser) return false;
      }
      // Only mine filter
      if (onlyMine && !spool.isMyOwn) return false;
      // Only non-empty filter
      if (onlyNonEmpty) {
        const remaining = parseFloat(spool.ostatok);
        if (isNaN(remaining) || remaining <= 0) return false;
      }
      return true;
    });
  }, [spools, onlyMine, onlyNonEmpty, searchQuery]);

  // Sort: my spools first, then by remaining (descending)
  const sortedSpools = useMemo(() => {
    return [...filteredSpools].sort((a, b) => {
      if (a.isMyOwn !== b.isMyOwn) return a.isMyOwn ? -1 : 1;
      return parseFloat(b.ostatok) - parseFloat(a.ostatok);
    });
  }, [filteredSpools]);

  const selectedSpool = spools?.find((s) => s.id === selectedSpoolId);
  const remaining = selectedSpool ? parseFloat(selectedSpool.ostatok) : 0;

  const handleSubmit = () => {
    const amountNum = parseFloat(amount);
    if (!selectedSpoolId || !amountNum || amountNum <= 0) return;

    recordUsage.mutate(
      {
        buhta_id: parseInt(selectedSpoolId),
        amount: amountNum,
        company: clientName,
      },
      {
        onSuccess: () => {
          setSelectedSpoolId('');
          setAmount('');
          onOpenChange(false);
        },
      }
    );
  };

  const handleClaimSpool = () => {
    if (!selectedSpoolId) return;
    transferOwnership.mutate(
      { buhtaId: parseInt(selectedSpoolId), username: '' },
      {
        onSuccess: () => {
          // Keep dialog open, spool will be refreshed
        },
      }
    );
  };

  const handleTransferSpool = () => {
    if (!selectedSpoolId || !transferTo) return;
    transferOwnership.mutate(
      { buhtaId: parseInt(selectedSpoolId), username: transferTo },
      {
        onSuccess: () => {
          setTransferTo('');
          setSelectedSpoolId('');
        },
      }
    );
  };

  const isValid =
    selectedSpoolId &&
    amount &&
    parseFloat(amount) > 0 &&
    parseFloat(amount) <= remaining;

  const isPending = recordUsage.isPending || transferOwnership.isPending;

  // Convert activeUsers object to array for dropdown
  const userOptions = useMemo(() => {
    if (!activeUsers) return [];
    return Object.entries(activeUsers).map(([username, displayName]) => ({
      username,
      displayName,
    }));
  }, [activeUsers]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cable className="h-5 w-5" />
            Расход кабеля
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2 overflow-y-auto flex-1">
          {/* Client name - readonly */}
          <div className="space-y-2">
            <Label className="text-muted-foreground">Клиент</Label>
            <div className="text-sm font-medium p-2 bg-muted rounded-md">
              {clientName}
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Поиск по номеру или сотруднику..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-9"
            />
          </div>

          {/* Filters */}
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={onlyMine}
                onChange={(e) => setOnlyMine(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              Только мои
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={onlyNonEmpty}
                onChange={(e) => setOnlyNonEmpty(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              Непустые
            </label>
          </div>

          {/* Spool list */}
          <div className="space-y-2">
            <Label>Выберите бухту</Label>
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : sortedSpools.length === 0 ? (
              <div className="flex items-center gap-2 py-4 text-muted-foreground">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">
                  {spools?.length === 0
                    ? 'Нет доступных бухт'
                    : 'Нет бухт по фильтрам'}
                </span>
              </div>
            ) : (
              <div className="border rounded-md max-h-48 overflow-y-auto">
                {sortedSpools.map((spool) => (
                  <SpoolItem
                    key={spool.id}
                    spool={spool}
                    isSelected={spool.id === selectedSpoolId}
                    onSelect={() => setSelectedSpoolId(spool.id)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Selected spool details & ownership */}
          {selectedSpool && (
            <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">#{selectedSpool.name}</div>
                  <div className="text-sm text-muted-foreground">
                    Остаток: {selectedSpool.ostatok}м
                  </div>
                </div>
                {selectedSpool.isMyOwn ? (
                  <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                    Моя
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {selectedSpool.username}
                  </span>
                )}
              </div>

              {/* Ownership controls */}
              {selectedSpool.isMyOwn ? (
                <div className="flex items-center gap-2">
                  <Select value={transferTo} onValueChange={setTransferTo}>
                    <SelectTrigger className="flex-1 h-8 text-sm">
                      <SelectValue placeholder="Отдать..." />
                    </SelectTrigger>
                    <SelectContent>
                      {userOptions.map((u) => (
                        <SelectItem key={u.username} value={u.username}>
                          {u.displayName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleTransferSpool}
                    disabled={!transferTo || isPending}
                  >
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  onClick={handleClaimSpool}
                  disabled={isPending}
                >
                  {transferOwnership.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  Забрать себе
                </Button>
              )}
            </div>
          )}

          {/* Amount input */}
          {selectedSpoolId && (
            <div className="space-y-2">
              <Label htmlFor="amount">
                Расход (метров)
              </Label>
              <Input
                id="amount"
                type="number"
                placeholder="Введите метраж..."
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min={1}
                max={remaining}
              />
              {amount && parseFloat(amount) > 0 && parseFloat(amount) <= remaining && (
                <p className="text-xs text-muted-foreground">
                  В бухте остается: {remaining - parseFloat(amount)}м
                </p>
              )}
              {parseFloat(amount) > remaining && (
                <p className="text-xs text-destructive">
                  Нельзя списать больше остатка ({remaining}м)
                </p>
              )}
            </div>
          )}

          {/* Submit button */}
          <Button
            className="w-full"
            onClick={handleSubmit}
            disabled={!isValid || isPending}
          >
            {recordUsage.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Cable className="h-4 w-4 mr-2" />
            )}
            Списать
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Spool list item component
function SpoolItem({
  spool,
  isSelected,
  onSelect,
}: {
  spool: CableSpool;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const remaining = parseFloat(spool.ostatok);
  const isEmpty = remaining <= 0;
  const isLow = remaining > 0 && remaining <= 50;
  const isWarning = remaining > 50 && remaining <= 100;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'w-full flex items-center gap-3 p-2 text-left hover:bg-accent transition-colors',
        'border-b last:border-b-0',
        isSelected && 'bg-accent',
        isEmpty && 'opacity-50'
      )}
    >
      {/* Radio indicator */}
      <div
        className={cn(
          'h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0',
          isSelected ? 'border-primary bg-primary' : 'border-muted-foreground'
        )}
      >
        {isSelected && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
      </div>

      {/* Spool info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium">#{spool.name}</span>
          <span
            className={cn(
              'text-sm',
              isEmpty && 'text-muted-foreground',
              isLow && 'text-red-600',
              isWarning && 'text-amber-600'
            )}
          >
            {spool.ostatok}м
          </span>
          {spool.isMyOwn && (
            <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">
              Моя
            </span>
          )}
        </div>
        {!spool.isMyOwn && (
          <div className="text-xs text-muted-foreground truncate">
            {spool.username}
          </div>
        )}
      </div>
    </button>
  );
}
